// Inline re-login modal shown when the axios response interceptor catches a
// 401 mid-session. The wizard state stays in memory, the user re-authenticates
// here, and they can immediately retry the failed action (Save, PDF export,
// schedule export, etc.) without losing any unsaved work.
import React, { useState } from "react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function SessionExpiredModal() {
  const { sessionExpired, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (!sessionExpired) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      await login(email, password);
      setPassword("");
      toast.success("Signed back in — please retry your previous action.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Login failed"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-background p-5 shadow-xl">
        <h2
          id="session-expired-title"
          className="text-lg font-semibold text-foreground"
        >
          Session expired
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your sign-in token has expired (tokens last 7 days). Your project
          state is still here — sign back in to continue. After login, retry
          the action that failed (Save, PDF, Excel, etc.).
        </p>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-foreground">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-9 w-full rounded border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="session-expired-email"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-medium text-foreground">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-9 w-full rounded border border-input bg-background px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="session-expired-password"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !email || !password}
            className="h-9 w-full rounded bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="session-expired-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
